import { useEffect, useState } from 'react';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { GgzoAddEditForm } from '../../GGZOint/v1/GgzoAddEditForm';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';

export function GgzoEmblV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const { patient } = useEpisodeContext();
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [emailKnown, setEmailKnown] = useState(false);
    const [questionnaire, setQuestionnaire] = useState<IEmbloomQuestionnaire | undefined>();

    if (!props.newProcessCustomIdentifier && !props.request.customControlData.vragenlijst) {
        return null;
    }

    useEffect(() => {
        if (patient.emailAddress && !props.request.customControlData.emailadres) {
            setEmailKnown(true);
            props.handleChangeCustomControl({ emailadres: patient.emailAddress });
        }

        if (props.request.customControlData.vragenlijst) {
            loadQuestionnaire(props.request.customControlData.vragenlijst);
        }
    }, []);

    useEffect(() => {
        if (props.newProcessCustomIdentifier) {
            props.handleChangeCustomControl({ vragenlijst: props.newProcessCustomIdentifier });
            loadQuestionnaire(props.newProcessCustomIdentifier!);
        }
    }, [props.newProcessCustomIdentifier]);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={questionnaire ? `${questionnaire.label} ${translateKey('request.request').toLowerCase()}` : ''}
            tooltip={getTooltipDescription()}
            tooltipHeader={questionnaire && 'Informatie over Ksyos Onderzoek'}
        >
            <GgzoAddEditForm
                {...props}
                buttonDisabled={buttonDisabled}
                emailKnown={emailKnown}
                saveRequest={saveRequest}
            />
        </NewOrEditRequestProcessContainer>
    );

    async function loadQuestionnaire(questionnaireId: string) {
        const result = await getJson(`/api/embloomQuestionnaires/${questionnaireId}`);
        setQuestionnaire(result.json);
        await props.handleChangeCustomControl({ vragenlijstLabel: result.json.label });
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                undefined,
                props.service,
                'request',
                `Het onderzoek is verstuurd naar ${getDisplayName(patient)}`
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        if (props.reloadPatient) {
            await props.reloadPatient();
        }
        await props.onSave(requestId);
    }

    function getTooltipDescription() {
        if (!questionnaire) {
            return;
        }

        if (!questionnaire.descriptionMedium) {
            const questionnaireType = questionnaire.isDecisionSupport ? 'beslisondersteuner' : 'vragenlijst';

            return `Er is momenteel geen beschrijving beschikbaar van deze ${questionnaireType}. Voor meer informatie kun je op de website van Embloom terecht.`;
        }

        return questionnaire.descriptionMedium;
    }
}

export function ggzoEmblV1RequestInitializeCustomControlData() {
    return {
        heeftNameting: false,
        nameting: null,
        emailadres: null,
        nametingOp: null,
        vragenlijst: null,
        vragenlijstLabel: null,
    };
}
